import { useCallback } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useCreateCoverDesignMutation, useDeleteCoverDesignMutation } from '../../../redux/api/fymAPI';

interface UseCoverVersionsProps {
  accountId: number;
  catalogItemUuid: string;
  /** The document on screen. */
  doc: CoverDoc;
  /** Saves the open version first. */
  flush: () => Promise<void>;
  onOpenDesign: (designUuid: string) => void;
}

/**
 * The version actions of the strip: "New" keeps the cover on screen as a new version
 * (`POST .../designs`) and opens it; a delete removes another version.
 */
export function useCoverVersions({ accountId, catalogItemUuid, doc, flush, onOpenDesign }: UseCoverVersionsProps) {
  const [createDesign, creating] = useCreateCoverDesignMutation();
  const [deleteDesign, deleting] = useDeleteCoverDesignMutation();

  const create = useCallback(() => {
    flush()
      .then(() => createDesign({ accountId, catalogItemUuid, doc }).unwrap())
      .then(({ design }) => onOpenDesign(design.uuid))
      .catch((error: unknown) => console.error('Cover editor: the new version was not saved', error));
  }, [accountId, catalogItemUuid, createDesign, doc, flush, onOpenDesign]);

  const remove = useCallback(
    (designUuid: string) => {
      deleteDesign({ accountId, catalogItemUuid, designUuid })
        .unwrap()
        .catch((error: unknown) => console.error('Cover editor: the version was not deleted', error));
    },
    [accountId, catalogItemUuid, deleteDesign]
  );

  return { create, remove, isBusy: creating.isLoading || deleting.isLoading };
}
